import { environment } from '@env/environment';

export class ApiConfig {
  private static readonly DEFAULT_URL_BASE = 'http://localhost';

  public static get url(): string {
    return environment.api.host;
  }

  public static get prefix(): string {
    return environment.api.prefix;
  }

  public static get authUrl(): string {
    return environment.auth.host;
  }

  public static get authPrefix(): string {
    return environment.auth.prefix;
  }

  public static isManagedUrl(url: string): boolean {
    const managedApiBaseUrl: URL | null = this.createManagedBaseUrl(ApiConfig.url, ApiConfig.prefix);
    const managedAuthBaseUrl: URL | null = this.createManagedBaseUrl(ApiConfig.authUrl, ApiConfig.authPrefix);
    const requestUrl: URL | null = this.parseUrl(url);

    if (!requestUrl || !managedApiBaseUrl || !managedAuthBaseUrl) {
      return false;
    }

    return this.isUrlInsideBase(requestUrl, managedApiBaseUrl) || this.isUrlInsideBase(requestUrl, managedAuthBaseUrl);
  }

  public static isAuthRefreshUrl(url: string, refreshPath: string): boolean {
    const managedAuthBaseUrl: URL | null = this.createManagedBaseUrl(ApiConfig.authUrl, ApiConfig.authPrefix);
    const requestUrl: URL | null = this.parseUrl(url);

    if (!requestUrl || !managedAuthBaseUrl) {
      return false;
    }

    const normalizedRefreshPath: string = this.normalizePath(refreshPath);
    const refreshEndpointUrl: URL = new URL(`${this.normalizePathname(managedAuthBaseUrl.pathname)}/${normalizedRefreshPath}`, managedAuthBaseUrl.origin);

    return requestUrl.origin === refreshEndpointUrl.origin && requestUrl.pathname === refreshEndpointUrl.pathname;
  }

  public static buildBaseUrl(base: 'api' | 'auth'): string {
    const rootUrl: string = base === 'auth' ? ApiConfig.authUrl : ApiConfig.url;
    const rootPrefix: string = base === 'auth' ? ApiConfig.authPrefix : ApiConfig.prefix;
    const managedBaseUrl: URL | null = this.createManagedBaseUrl(rootUrl, rootPrefix);

    if (!managedBaseUrl) {
      return `${rootUrl}${rootPrefix}`;
    }

    return `${managedBaseUrl.origin}${this.normalizePathname(managedBaseUrl.pathname)}`;
  }

  private static createManagedBaseUrl(rootUrl: string, rootPrefix: string): URL | null {
    const baseUrl: URL | null = this.parseUrl(rootUrl);

    if (!baseUrl) {
      return null;
    }

    const normalizedPrefix: string = this.normalizePath(rootPrefix);
    const pathname: string = normalizedPrefix.length > 0 ? `/${normalizedPrefix}` : '/';

    return new URL(pathname, baseUrl.origin);
  }

  private static parseUrl(url: string): URL | null {
    try {
      return new URL(url);
    } catch {
      try {
        return new URL(url, ApiConfig.DEFAULT_URL_BASE);
      } catch {
        return null;
      }
    }
  }

  private static isUrlInsideBase(requestUrl: URL, baseUrl: URL): boolean {
    if (requestUrl.origin !== baseUrl.origin) {
      return false;
    }

    const basePathname: string = this.normalizePathname(baseUrl.pathname);
    const requestPathname: string = this.normalizePathname(requestUrl.pathname);

    return requestPathname === basePathname || requestPathname.startsWith(`${basePathname}/`);
  }

  private static normalizePath(path: string): string {
    return path.replace(/^\/+|\/+$/g, '').trim();
  }

  private static normalizePathname(pathname: string): string {
    const normalizedPath: string = this.normalizePath(pathname);
    return normalizedPath.length > 0 ? `/${normalizedPath}` : '/';
  }
}
